import React, { useState } from "react";
import { formatDateTime } from "../../content_panel/utils/helpers";
import { InwardAttachmentList } from "./InwardAttachmentList";
import { LinkedInwardModel } from "../model/warehouse_inward_models";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp, mdiPackageVariantClosed } from "@mdi/js";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";

interface LinkedInwardsTableProps {
  title?: string;
  data: LinkedInwardModel[];
}

const LinkedInwardsTable: React.FC<LinkedInwardsTableProps> = ({
  title = "Linked Inwards",
  data,
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  if (!data?.length) return null;

  const toggleRow = (refId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [refId]: !prev[refId],
    }));
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>

      {data.map((row, idx) => (
        <div
          key={idx}
          id={`warehouse-inward-linked-row-${row.ref_id}`}
          className="border rounded-lg p-4 space-y-3 bg-white shadow-sm"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-bold text-blue-700">{row.ref_id}</span>
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-green-50 text-green-700 border-green-200">
                {row.type === "GRN" ? "GRN" : "Stock Transfer"}
              </span>
            </div>

            <div className="text-right text-sm text-gray-500">
              <div>{formatDateTime(row.date)}</div>
              {row.created_by_name && (
                <div className="text-xs text-gray-400">
                  by {row.created_by_name}
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm bg-gray-50 p-3 rounded-md">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                Unique Items
              </p>
              <p className="font-medium text-gray-900">
                {row?.items_count ?? 0}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                Total Accepted
              </p>
              <p className="font-medium text-green-600">
                {row?.accepted_qty ?? 0}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                Total Value
              </p>
              <p className="font-medium text-gray-900">
                ₹{row?.value?.toLocaleString() ?? "0"}
              </p>
            </div>
          </div>

          {/* Collapsible Items Section */}
          <div className="border rounded-md overflow-hidden">
            <button
              id={`warehouse-inward-linked-toggle-${row.ref_id}`}
              onClick={() => toggleRow(row.ref_id)}
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium"
            >
              <div className="flex items-center gap-2">
                <Icon
                  path={mdiPackageVariantClosed}
                  size={0.7}
                  className="text-orange-600"
                />
                <span>View Received Items ({row.items?.length ?? 0})</span>
              </div>
              <Icon
                path={expandedRows[row.ref_id] ? mdiChevronUp : mdiChevronDown}
                size={0.8}
              />
            </button>

            {expandedRows[row.ref_id] && (
              <div className="p-3 bg-white divide-y divide-gray-100">
                {row.items?.map((item, itemIdx) => (
                  <div key={itemIdx} className="py-2 first:pt-0 last:pb-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.item_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.item_code}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {item.accepted_qty} Qty
                        </p>
                        <p className="text-xs text-gray-500">
                          ₹{item.value?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {item.device_ids && item.device_ids.length > 0 && (
                      <DeviceIdsViewer
                        deviceIds={item.device_ids}
                        title="InwardedDevice IDs"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invoice Attachments */}
          {row.attachments && row.attachments.length > 0 && (
            <div className="pt-2">
              <InwardAttachmentList
                attachments={row.attachments.map((a) => ({
                  ...a,
                  voucher_id: row.ref_id, // Map ref_id to voucher_id for InwardAttachmentList
                  id: a.filename, // Temporary ID
                  category: "inward_attachment",
                  size: 0,
                }))}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default LinkedInwardsTable;
